import { useAuth } from '@/contexts/AuthContext';
import { buildForecast } from '@/lib/cashflow-forecast';
import { formatAUD } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ReferenceLine } from 'recharts';
import { TrendingDown, TrendingUp, ArrowLeft, Calendar } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const chartConfig = {
  cumulative: { label: 'Balance Change', color: 'hsl(var(--primary))' },
  projected: { label: 'Daily Net', color: 'hsl(var(--accent))' },
};

export default function CashFlowPage() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState<30 | 60 | 90>(30);

  const forecast = useMemo(
    () => buildForecast(data.transactions, data.bills, days),
    [data.transactions, data.bills, days],
  );

  const chartData = useMemo(
    () =>
      forecast.days.map((d) => ({
        date: new Date(d.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
        rawDate: d.date,
        cumulative: Math.round(d.cumulative),
        projected: Math.round(d.projected),
        label: d.label,
      })),
    [forecast.days],
  );

  const minDay = useMemo(() => {
    let min = forecast.days[0];
    for (const d of forecast.days) {
      if (d.cumulative < min.cumulative) min = d;
    }
    return min;
  }, [forecast.days]);

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-display text-2xl font-bold">Cash Flow Forecast</h1>
          <p className="text-sm text-muted-foreground">Projected balance based on your history &amp; upcoming bills</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="mb-4 flex gap-2">
        {([30, 60, 90] as const).map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? 'default' : 'outline'}
            onClick={() => setDays(d)}
          >
            {d} days
          </Button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Income</p>
            <p className="text-sm font-bold text-success">{formatAUD(forecast.totalIncome)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Expenses</p>
            <p className="text-sm font-bold text-destructive">{formatAUD(forecast.totalExpenses)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Net</p>
            <p className={cn('text-sm font-bold', forecast.netChange >= 0 ? 'text-success' : 'text-destructive')}>
              {formatAUD(forecast.netChange)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Warning */}
      {minDay.cumulative < -50 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2">
          <TrendingDown className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-xs text-destructive">
            Potential squeeze around{' '}
            {new Date(minDay.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
            {minDay.label ? ` (${minDay.label})` : ''} — cumulative dip of {formatAUD(Math.abs(minDay.cumulative))}
          </p>
        </div>
      )}

      {/* Tabs: Chart / Table */}
      <Tabs defaultValue="chart">
        <TabsList className="w-full mb-3">
          <TabsTrigger value="chart" className="flex-1">Chart</TabsTrigger>
          <TabsTrigger value="table" className="flex-1">Day-by-Day</TabsTrigger>
        </TabsList>

        <TabsContent value="chart">
          <Card>
            <CardContent className="p-2 pt-4">
              <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fillCumulative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10 }}
                    interval={Math.floor(chartData.length / 6)}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: number) => `$${Math.round(v)}`}
                    width={55}
                  />
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeOpacity={0.5} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value, name) => (
                          <span className="font-mono text-xs">
                            {formatAUD(value as number)}
                          </span>
                        )}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulative"
                    stroke="hsl(var(--primary))"
                    fill="url(#fillCumulative)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="table">
          <Card>
            <CardContent className="p-0">
              <div className="max-h-[400px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky top-0 bg-background">Date</TableHead>
                      <TableHead className="sticky top-0 bg-background text-right">Daily Net</TableHead>
                      <TableHead className="sticky top-0 bg-background text-right">Cumulative</TableHead>
                      <TableHead className="sticky top-0 bg-background">Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forecast.days.map((d, i) => (
                      <TableRow key={i} className={cn(d.label && 'bg-accent/30')}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {new Date(d.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </TableCell>
                        <TableCell className={cn('text-xs text-right font-mono', d.projected >= 0 ? 'text-success' : 'text-destructive')}>
                          {formatAUD(Math.round(d.projected))}
                        </TableCell>
                        <TableCell className={cn('text-xs text-right font-mono', d.cumulative >= 0 ? 'text-success' : 'text-destructive')}>
                          {formatAUD(Math.round(d.cumulative))}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">
                          {d.label || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="mt-4 text-xs text-muted-foreground text-center">
        Based on your last 60 days of income &amp; expenses
      </p>
    </div>
  );
}
